/**
 * useSnapTradeConnection
 *
 * Manages the SnapTrade OAuth flow state. The actual portal UI is rendered
 * by <SnapTradePortal> (platform-specific: iframe on web, WebView modal on native).
 *
 *   1. User calls `open()` → fetches oauthUrl from API, sets isOpen=true
 *   2. <SnapTradePortal> renders with the URL
 *   3. On SUCCESS postMessage → `handleSuccess(authorizationId)` → authorize API call
 *   4. On close/exit → `close()`
 */

import { useCallback, useState } from 'react';
import { usePathname } from 'expo-router';
import { trpc } from '../lib/trpc';
import { trackConnectionLinkEvent } from '../lib/product-events';

export interface SnapTradeConnectionOptions {
  onSuccess: (connectionId: string) => void;
  onError?: (error: string) => void;
}

export function useSnapTradeConnection({
  onSuccess,
  onError,
}: SnapTradeConnectionOptions) {
  const pathname = usePathname();
  const [oauthUrl, setOauthUrl] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createLinkTokenMutation = trpc.connections.createLinkToken.useMutation();
  const authorizeMutation = trpc.connections.authorize.useMutation();

  const open = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    trackConnectionLinkEvent({
      route: pathname,
      screen: 'connections',
      provider: 'snaptrade',
      status: 'started',
    });
    try {
      const result = await createLinkTokenMutation.mutateAsync({ provider: 'snaptrade' });
      if (!result.oauthUrl) throw new Error('No SnapTrade OAuth URL returned');
      setOauthUrl(result.oauthUrl);
      setIsOpen(true);
      trackConnectionLinkEvent({
        route: pathname,
        screen: 'connections',
        provider: 'snaptrade',
        status: 'opened',
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start brokerage connection';
      trackConnectionLinkEvent({
        route: pathname,
        screen: 'connections',
        provider: 'snaptrade',
        status: 'failed',
        metadata: {
          stage: 'open',
          errorMessage: msg,
        },
      });
      setError(msg);
      onError?.(msg);
    } finally {
      setIsLoading(false);
    }
  }, [createLinkTokenMutation, onError, pathname]);

  const close = useCallback(() => {
    setIsOpen(false);
    setOauthUrl(null);
    trackConnectionLinkEvent({
      route: pathname,
      screen: 'connections',
      provider: 'snaptrade',
      status: 'closed',
    });
  }, [pathname]);

  const handleSuccess = useCallback(
    async (authorizationId: string) => {
      setIsOpen(false);
      setOauthUrl(null);
      setIsLoading(true);
      try {
        const result = await authorizeMutation.mutateAsync({
          provider: 'snaptrade',
          publicToken: authorizationId,
        });
        trackConnectionLinkEvent({
          route: pathname,
          screen: 'connections',
          provider: 'snaptrade',
          status: 'succeeded',
        });
        onSuccess(result.connectionId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to connect brokerage';
        trackConnectionLinkEvent({
          route: pathname,
          screen: 'connections',
          provider: 'snaptrade',
          status: 'failed',
          metadata: {
            stage: 'authorize',
            errorMessage: msg,
          },
        });
        setError(msg);
        onError?.(msg);
      } finally {
        setIsLoading(false);
      }
    },
    [authorizeMutation, onSuccess, onError, pathname],
  );

  const handleError = useCallback(
    (detail: string) => {
      setIsOpen(false);
      setOauthUrl(null);
      trackConnectionLinkEvent({
        route: pathname,
        screen: 'connections',
        provider: 'snaptrade',
        status: 'failed',
        metadata: {
          stage: 'portal',
          errorMessage: detail,
        },
      });
      setError(detail);
      onError?.(detail);
    },
    [onError, pathname],
  );

  return {
    open,
    close,
    handleSuccess,
    handleError,
    oauthUrl,
    isOpen,
    isLoading: isLoading || createLinkTokenMutation.isPending || authorizeMutation.isPending,
    error,
  };
}
